import React, { useState } from 'react';
import { accountService } from '../../services/api/accountService';
import LoadingOverlay from '../common/LoadingOverlay';
import AuthCard from '../common/AuthCard/AuthCard';
import {
  Field,
  FieldError,
  FieldLabel,
  Form,
  StatusMessage,
  SubmitButton,
  TextInput,
  TextLink,
} from '../common/AuthCard/AuthCard.styled';

export const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackVariant, setFeedbackVariant] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (value) => {
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    return isValid ? '' : 'Please enter a valid email address.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setEmailError('');
    setFeedback('');
    setIsSubmitting(true);

    try {
      const response = await accountService.sendPasswordResetLink(email);

      if (response.status === 204) {
        setFeedbackVariant('success');
        setFeedback(
          'If an account exists for this email, a reset link has been sent. It will expire in 15 minutes.'
        );
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (err) {
      console.error('Error requesting password reset:', err);
      setFeedbackVariant('error');
      setFeedback('Oops! Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AuthCard
        title="Reset your password"
        lead="Enter your email and we’ll send you a link to reset your password."
        footer={
          <p>
            Remember your password? <TextLink to="/login">Sign in</TextLink>
          </p>
        }
      >
        <Form onSubmit={handleSubmit} noValidate>
          <Field>
            <FieldLabel htmlFor="forgot-password-email">Email</FieldLabel>
            <TextInput
              id="forgot-password-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailError(validateEmail(email))}
              required
              disabled={isSubmitting}
              maxLength={100}
              autoComplete="email"
              aria-invalid={Boolean(emailError)}
              aria-describedby={emailError ? 'forgot-password-email-error' : undefined}
            />
            {emailError && (
              <FieldError id="forgot-password-email-error">{emailError}</FieldError>
            )}
          </Field>
          {feedback && (
            <StatusMessage
              $variant={feedbackVariant}
              role={feedbackVariant === 'error' ? 'alert' : 'status'}
            >
              {feedback}
            </StatusMessage>
          )}
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send reset link'}
          </SubmitButton>
        </Form>
      </AuthCard>
      {isSubmitting && <LoadingOverlay />}
    </>
  );
};
